import { useCallback, useEffect, useState } from 'react';

import { ApiError } from '../api/client';

interface AsyncState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
  setData: (value: T) => void;
}

/**
 * Chargement de données avec état d'erreur lisible.
 *
 * Le message affiché est celui du serveur : les erreurs métier sont déjà
 * rédigées en langage humain côté API (brief §17).
 */
export function useAsync<T>(loader: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  // `deps` est fourni par l'appelant et joue le rôle du tableau de
  // dépendances : c'est volontaire, et chaque appel passe un tableau de
  // taille constante, condition requise par React.
  const run = useCallback(loader, deps);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    run()
      .then((value) => {
        if (active) setData(value);
      })
      .catch((cause) => {
        if (!active) return;
        setError(cause instanceof ApiError ? cause.detail : 'Erreur inattendue.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [run, tick]);

  return {
    data,
    error,
    loading,
    reload: () => setTick((value) => value + 1),
    setData,
  };
}
